import DOMPurify from 'dompurify';
import React, { useEffect } from 'react';
import parse from 'html-react-parser';
import { observer } from 'mobx-react-lite';

import {
  Accordion,
  Hero,
  PassolutionHeader,
} from '@cfm-media/pwa-ui-components';
import { LoadableStatus } from '../mobx-utils';
import { i18next, useTranslation } from '../core/utils/translations';
import { useStore } from '../core/store';

import { ContentWrapper } from '../components/content-wrapper';
import { ErrorPage } from '../components/error-page';
import { LoadSpinner } from '../components/loader';
import { PageWrapper } from '../components/page-wrapper';
import { getUniqueCountries } from '../utils/passolution';
import { urls } from '../urls';

export const EntryConditions = observer(() => {
  const { t } = useTranslation(['entry-regulations-page', 'error-pages']);

  const { booking, entryConditions, options, selection } = {
    ...useStore().BookingStore,
    ...useStore().passolution,
  };

  const { nationality, setNationality } = selection;
  const hotels = booking?.data?.hotels;
  const orig = 'de';
  const date = new Date();
  const lng = i18next.language;
  const bookingDestinationsNames = hotels?.map((hotel) => hotel?.country);

  /**
   * useEffect used to handle pre-selection and fetching.
   */
  useEffect(() => {
    // ensure booking data is loaded
    if (booking?.status === LoadableStatus.done) {
      loadOptions().then(() => {
        setNationality('DE');
        fetch('DE');
      });
    }
  }, [booking?.status]);

  /**
   * Fetches passolution options if not already present.
   */

  const loadOptions = async () => {
    if (!options.nationalities.length) {
      await options.fetch(i18next.language);
    }
  };

  /**
   * Fetches passolution data.
   */
  const fetch = async (nat) => {
    const uniqueBookingDestinations = getUniqueCountries(
      booking?.data?.hotels,
      booking?.data?.flights,
    );

    entryConditions.fetch(uniqueBookingDestinations, nat, orig, lng, date);
  };

  // prettier-ignore
  if ([booking?.status, entryConditions?.status].some((status) => status === LoadableStatus.error)) {
      return <PassolutionError />;
  } else if ([booking?.status, entryConditions?.status].some((status) => status !== LoadableStatus.done)) {
      return <LoadSpinner />;
  }

  const result = entryConditions?.data[0];
  const destinationName = bookingDestinationsNames[0];
  const throttlingError = result?.error?.code === 429;

  const firstCountries = [
    { code: 'DE', name: 'Deutschland' },
    { code: 'CH', name: 'Schweiz' },
    { code: 'AT', name: 'Österreich' },
    { code: 'NL', name: 'Niederlande' },
  ];

  const nationalitySorted = [...firstCountries, ...options?.nationalities];

  const nationalitySortedUnique = [
    ...new Map(nationalitySorted.map((item) => [item['name'], item])).values(),
  ];

  return (
    <PageWrapper>
      {result?.error ||
      !entryConditions?.data?.length ||
      entryConditions?.status === LoadableStatus.error ? (
        <>
          <ErrorPage
            title={t('error-pages:entry-conditions-error-title-text')}
            body={
              throttlingError
                ? t('error-pages:entry-conditions-error-throttling-body-text')
                : t('error-pages:entry-conditions-error-body-text')
            }
            link={urls.home}
            buttonText={t('error-pages:entry-conditions-error-button-text')}
            icon={'icon-passport'}
          />
        </>
      ) : (
        <>
          <PassolutionHeader
            image="/public/assets/images/teasers/passolution.jpg"
            iconClass="icon-passport"
            headline={destinationName}
            overlay
            isMultiDestination={false}
          />

          <ContentWrapper>
            <div className="hidden lg:block mb-3 text-xl text-brand-1 font-semibold uppercase">
              {t('sub-headline')}
            </div>

            <label
              className="block text-sm font-medium text-gray-700"
              htmlFor="nationality"
            >
              {t('nationality')}
            </label>

            <select
              className="custom-select w-full px-3 py-3 block border border-gray-300 text-sm placeholder-gray-400 appearance-none focus:outline-none focus:ring-brand-1 focus:border-brand-1"
              name="nationality"
              value={nationality}
              onChange={(event) => {
                setNationality(event.target.value), fetch(event.target.value);
              }}
            >
              <Options options={nationalitySortedUnique} />
            </select>
            <br />
            <Accordion
              headline={result?.entry?.headline}
              content={parse(
                DOMPurify.sanitize(fixLineBreaks(result?.entry?.content)),
              )}
            />

            <Accordion
              headline={result?.inoculation?.headline}
              content={parse(
                DOMPurify.sanitize(fixLineBreaks(result?.inoculation?.content)),
              )}
            />

            <Accordion
              headline={result?.visa?.headline}
              content={parse(
                DOMPurify.sanitize(fixLineBreaks(result?.visa?.content)),
              )}
            />

            <Accordion
              headline={result?.transitvisa?.headline}
              content={parse(
                DOMPurify.sanitize(fixLineBreaks(result?.transitvisa?.content)),
              )}
            />
          </ContentWrapper>
        </>
      )}
    </PageWrapper>
  );
});

const Options = observer(({ options }) => {
  return (
    <>
      {options.map((option) => (
        <option key={option.code} value={option.code}>
          {option.name}
        </option>
      ))}
    </>
  );
});

const fixLineBreaks = (htmlString) => htmlString.replaceAll('\n', '<br>');

export const PassolutionError = () => (
  <PageWrapper>
    <Hero
      image="https://1avista-pwa.cfmmedia.de/d6b9b0b02ed9f0548107.jpg"
      iconClass="icon-passport"
      headline="Einreiseinformationen"
      overlay
    />
    <ContentWrapper>
      <ErrorPage
        title="Keine Einreiseinformationen gefunden"
        body="Es wurden keine Einreiseinformationen für diese Buchung gefunden."
        link={urls.home}
        buttonText="Zurück zur Startseite"
      />
    </ContentWrapper>
  </PageWrapper>
);
